import { MediaUploadError } from '@/hooks/use-media-upload.ts';
import i18n from '@/i18n/i18n';
import type { MediaUploadPreflightResponse } from '@/types/dtos/media/media-upload.dto.ts';
import { getMediaUploadErrorCode } from '@/helpers/media-upload-quota.helper.ts';
import { resolveMediaUploadSizeLimitViolationMessage } from '@/helpers/media-upload-size-limit.helper.ts';
import type { FileType } from '@/types/dtos/media/media.asset.dto.ts';
import type { TranslationKey } from '@/i18n/translation-key';

export function isMediaUploadPreflightBlocked(preflight: MediaUploadPreflightResponse, fileSizeBytes: number): boolean {
  if (!preflight.allowed) {
    return true;
  }
  if (preflight.errorCode === 'FILE_TOO_LARGE') {
    return true;
  }
  return preflight.maxFileSizeBytes != null && fileSizeBytes > preflight.maxFileSizeBytes;
}

export function createPreflightBlockedUploadError(
  preflight: MediaUploadPreflightResponse,
  fileSizeBytes?: number,
  fileName?: string,
): MediaUploadError {
  const isFileTooLarge =
    preflight.errorCode === 'FILE_TOO_LARGE' ||
    (preflight.maxFileSizeBytes != null && fileSizeBytes != null && fileSizeBytes > preflight.maxFileSizeBytes);
  const errorCode = preflight.errorCode ?? (isFileTooLarge ? 'FILE_TOO_LARGE' : 'INSUFFICIENT_STORAGE_SPACE');
  let message = errorCode === 'FILE_TOO_LARGE' ? 'File exceeds the maximum upload size' : 'Insufficient upload quota';

  if (errorCode === 'FILE_TOO_LARGE' && preflight.maxFileSizeBytes != null) {
    const sizeBytes = fileSizeBytes ?? preflight.requiredBytes;
    message = resolveMediaUploadSizeLimitViolationMessage(
      {
        fileSizeBytes: sizeBytes,
        maxFileSizeBytes: preflight.maxFileSizeBytes,
        fileType: preflight.details?.fileType as FileType | undefined,
        fileName: fileName ?? preflight.details?.fileName,
        imageSequenceTotal: preflight.details?.imageSequenceTotal,
      },
      (key, params) => String(i18n.t(key, params)),
    );
  }

  return new MediaUploadError(message, errorCode, {
    kind: 'quota_blocked',
    preflight,
  });
}

export function resolveMediaUploadBlockedMessage(
  preflight: MediaUploadPreflightResponse,
  fileSizeBytes: number,
  fileName: string,
  t: (key: TranslationKey, params?: Record<string, string | number>) => string,
): string {
  const isFileTooLarge =
    preflight.errorCode === 'FILE_TOO_LARGE' ||
    (preflight.maxFileSizeBytes != null && fileSizeBytes > preflight.maxFileSizeBytes);

  if (isFileTooLarge && preflight.maxFileSizeBytes != null) {
    return resolveMediaUploadSizeLimitViolationMessage(
      {
        fileSizeBytes: fileSizeBytes ?? preflight.requiredBytes,
        maxFileSizeBytes: preflight.maxFileSizeBytes,
        fileType: preflight.details?.fileType as FileType | undefined,
        fileName: preflight.details?.fileName ?? fileName,
        imageSequenceTotal: preflight.details?.imageSequenceTotal,
      },
      t,
    );
  }

  return t('media.uploadPreflight.quotaInsufficient');
}

/** Keep inline upload feedback visible after a preflight rejection (do not call reset). */
export function getMediaUploadErrorMessage(
  error: unknown,
  t: (key: TranslationKey, params?: Record<string, string | number>) => string,
): string | undefined {
  if (error instanceof MediaUploadError) {
    if (error.errorCode === 'FILE_TOO_LARGE' && error.message) {
      return error.message;
    }
    if (error.errorCode) {
      return t(`error.${error.errorCode}` as TranslationKey);
    }
  }

  const errorCode = getMediaUploadErrorCode(error);
  if (errorCode) {
    return t(`error.${errorCode}` as TranslationKey);
  }

  return undefined;
}

/** Keep inline upload feedback visible after a preflight rejection (do not call reset). */
export function shouldPreserveMediaUploadFeedback(error: unknown): boolean {
  if (error instanceof MediaUploadError && error.quotaBlocked) {
    return true;
  }
  const errorCode = getMediaUploadErrorCode(error);
  return errorCode === 'FILE_TOO_LARGE' || errorCode === 'INSUFFICIENT_STORAGE_SPACE';
}
